import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { shareReplay, map } from 'rxjs/operators';

export interface IDashboardMetricsState {
  charts: number[];
  metrics: string[];
}

export interface IDashboardChartPallete {
  borderColor: string;
  chartBgColor: string;
  chartBgLightColor: string;
}

export const chartPalettes: IDashboardChartPallete[] = [
  {
    borderColor: 'chart-border-1',
    chartBgColor: '--bs-chart-line-1',
    chartBgLightColor: 'var(--bs-chart-bg-1)'
  },
  {
    borderColor: 'chart-border-2',
    chartBgColor: '--bs-chart-line-2',
    chartBgLightColor: 'var(--bs-chart-bg-2)'
  }
];

@Injectable({
  providedIn: 'root',
})
export class DashboardCardService {
  private _selectedCharts$: BehaviorSubject<number[]> = new BehaviorSubject<number[]>([]);
  private _selectedMetrics$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  private _selectedChartsColors$: BehaviorSubject<Map<number, IDashboardChartPallete>> = new BehaviorSubject<Map<number, IDashboardChartPallete>>(new Map());

  constructor() {
    this._selectedCharts$.subscribe(charts => {
      const newPalletes = this._selectedChartsColors$.value;

      // Remove not selected items
      const selectedKeys = [...this._selectedChartsColors$.value.keys()];
      selectedKeys.forEach(key => {
        if (!charts.includes(key)) {
          newPalletes.delete(key);
        }
      });

      // Get list of available palettes
      const selectedPalettes = [...newPalletes.values()].map(palette => palette.borderColor);
      const availablePalettes = chartPalettes.filter(palette => !selectedPalettes.includes(palette.borderColor))

      // Add new items
      charts.map(chartIndex => {
        if (!newPalletes.has(chartIndex)) {
          const palette = availablePalettes.shift();
          if (palette) {
            newPalletes.set(chartIndex, palette)
          }
        }
      });

      this._selectedChartsColors$.next(newPalletes);

    });
  }

  public get selectedCharts$(): Observable<number[]> {
    return this._selectedCharts$
      .asObservable()
      .pipe(shareReplay());
  }

  public get selectedMetrics$(): Observable<string[]> {
    return this._selectedMetrics$
      .asObservable()
      .pipe(shareReplay());
  }

  public get selectedChartsColors$(): Observable<Map<number, IDashboardChartPallete>> {
    return this._selectedChartsColors$
      .asObservable()
      .pipe(shareReplay());
  }


  public get state$(): Observable<IDashboardMetricsState> {
    return combineLatest([this.selectedCharts$, this.selectedMetrics$])
      .pipe(
        map(([charts, metrics]) => ({ charts, metrics })),
        shareReplay(),
      );
  }

  public get selectedChartsSnapshot(): number[] {
    return this._selectedCharts$.value;
  }

  public get selectedMetricsSnapshot(): string[] {
    return this._selectedMetrics$.value;
  }

  public get selectedChartNamesSnapshot(): string[] {
    return this._selectedCharts$.value.map(idx => this._selectedMetrics$.value[idx]);
  }

  public toggleSelectedChart(chartIdx: number): void {
    if (chartIdx >= this._selectedMetrics$.value.length) {
      return;
    }

    const selectedCharts = this._selectedCharts$.value;
    const idx = selectedCharts.indexOf(chartIdx);

    if (idx === -1 && selectedCharts.length < 2) {
      this.setSelectedCharts([ ...selectedCharts, chartIdx]);
    } else if (idx >= 0 && selectedCharts.length > 1) {
      this.setSelectedCharts(
        selectedCharts.filter((_, chartIdx) => chartIdx !== idx)
      );
    }
  }

  public setSelectedCharts(selectedCharts: number[]): void {
    this._selectedCharts$.next(selectedCharts);
  }

  public setSelectedMetrics(selectedMetrics: string[]): void {
    this._selectedMetrics$.next(selectedMetrics);
  }

  public saveStateToLocalStorage(key: string): void {
    if (!key) {
      return;
    }

    localStorage.setItem(key, JSON.stringify({
      charts: this.selectedChartsSnapshot,
      metrics: this.selectedMetricsSnapshot,
    }));
  }

  public tryInitFromLocalStorage(
    key: string,
    initChartsFallback: number[],
    initMetricsFallback: string[],
    allMetrics: string[],
  ): void {
    this.resetData();

    let charts: number[] = initChartsFallback;
    let metrics: string[] = initMetricsFallback;

    if (key) {
      try {
        const savedStateRaw = localStorage.getItem(key);

        if (savedStateRaw) {
          const savedState = JSON.parse(savedStateRaw);

          const isLSStateValid = savedState.charts.length === 2 &&
            savedState.charts.every((chart: number) => Number.isFinite(chart) && Number.isInteger(chart) && chart >= 0) &&
            savedState.metrics.every((metric: string) => allMetrics.includes(metric));

          if (isLSStateValid) {
            charts = savedState.charts;
            metrics = savedState.metrics;
          } else {
            localStorage.removeItem(key);
          }
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[DashboardCardService] State in LS is empty or cannot be parsed');
      }
    }

    this.setSelectedCharts(charts);
    this.setSelectedMetrics(metrics);
  }

  private resetData() {
    this._selectedCharts$.next([]);
    this._selectedMetrics$.next([]);
    this._selectedChartsColors$.next(new Map());
  }
}
